/**
 * Accès au flux de réservations GMS (proxy Quilium `/fr/api-rentable`).
 *
 * Point d'entrée UNIQUE, partagé par le calendrier de la fiche salle
 * (`_fullCalendar.js`) et par les pickers du formulaire (`flatpickr-init-app.js`),
 * pour qu'ils ne puissent plus diverger.
 *
 * Stratégie : chargement **à la demande**. On ne récupère que la fenêtre réellement
 * nécessaire (mois affichés, ou plage sélectionnée par l'usager), et un cache par
 * intervalles évite de redemander ce qui est déjà connu. Naviguer d'un mois à l'autre
 * ne coûte donc qu'un appel sur la portion inédite — souvent zéro.
 *
 * ⚠️ Les paramètres DOIVENT s'appeler `start` et `end` : le page-type Quilium
 * `apiRentFeed` interpole `{{query.start}}` / `{{query.end}}` dans l'URL GMS.
 * Tout autre nom laisse l'appel partir en `feed?start=&end=`, l'API renvoie alors
 * ses 100 réservations les PLUS ANCIENNES, et le calendrier se retrouve vide.
 */

// Le flux plafonne à 100 enregistrements par réponse et ne trie pas par date :
// au-delà, les réservations sont tronquées SANS erreur ni indicateur. Toute réponse
// pleine est donc redécoupée en deux, récursivement.
const FEED_RESPONSE_LIMIT = 100;
// Simple garde-fou : la récursion s'arrête d'elle-même dès qu'une tranche fait un jour
// (le point médian rejoint alors la borne de début). 12 niveaux = 4096 feuilles ; sur
// l'horizon de 3 ans du formulaire (~1220 jours), descendre à la journée en demande 11.
const MAX_SPLIT_DEPTH = 12;

const DAY_MS = 24 * 60 * 60 * 1000;

/** rentableId -> { ranges: [[startMs, endMs]], rows: [], byKey: Set } */
const cache = new Map();
/** URL -> Promise, pour ne pas lancer deux fois le même appel en vol. */
const inFlight = new Map();

function isoDay(date) {
    return (
        date.getFullYear() +
        '-' +
        String(date.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(date.getDate()).padStart(2, '0')
    );
}

/** Minuit local, pour raisonner en jours pleins. */
function dayStart(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

function entry(rentableId) {
    if (!cache.has(rentableId)) {
        cache.set(rentableId, { ranges: [], rows: [], byKey: new Set() });
    }
    return cache.get(rentableId);
}

/**
 * Portions de [startMs, endMs] pas encore chargées pour ce rentable.
 */
function missingRanges(store, startMs, endMs) {
    let gaps = [[startMs, endMs]];
    store.ranges.forEach(([ls, le]) => {
        const next = [];
        gaps.forEach(([gs, ge]) => {
            if (le < gs || ls > ge) { next.push([gs, ge]); return; }   // aucun recouvrement
            if (ls > gs) next.push([gs, ls - DAY_MS]);                 // reste à gauche
            if (le < ge) next.push([le + DAY_MS, ge]);                 // reste à droite
        });
        gaps = next.filter(([s, e]) => s <= e);
    });
    return gaps;
}

/**
 * Enregistre un intervalle comme chargé, en fusionnant les intervalles contigus.
 */
function rememberRange(store, startMs, endMs) {
    store.ranges.push([startMs, endMs]);
    store.ranges.sort((a, b) => a[0] - b[0]);
    const merged = [store.ranges[0]];
    for (let i = 1; i < store.ranges.length; i++) {
        const last = merged[merged.length - 1];
        const cur = store.ranges[i];
        if (cur[0] <= last[1] + DAY_MS) last[1] = Math.max(last[1], cur[1]);
        else merged.push(cur);
    }
    store.ranges = merged;
}

function addRows(store, rows) {
    rows.forEach((r) => {
        const key = (r && r.id) + '|' + (r && r.start) + '|' + (r && r.end);
        if (!store.byKey.has(key)) {
            store.byKey.add(key);
            store.rows.push(r);
        }
    });
}

// Un appel qui n'aboutit pas au bout de ce délai est abandonné. Sans lui, une
// connexion ouverte mais muette laissait `Promise.all` en suspens pour toujours :
// les champs de date restaient désactivés et le formulaire, inutilisable, sans
// qu'aucun message n'explique pourquoi.
const FEED_TIMEOUT_MS = 10000;

/**
 * Récupère une tranche, en la redécoupant si la réponse est pleine (anti-troncature).
 *
 * @returns {Promise<{ok: boolean, rows: Array}>} `ok: false` = on ne SAIT PAS ce que
 *   contient cette tranche. C'est la distinction qui manquait : un `.catch(() => [])`
 *   rendait un tableau vide, que l'appelant lisait « aucune réservation ». Une coupure
 *   réseau de deux secondes affichait donc un samedi complet comme entièrement libre,
 *   et la tranche était en plus mémorisée comme chargée — l'erreur devenait définitive
 *   pour la session. Le module voisin `materialAvailability.js` applique déjà cette
 *   règle : « je ne sais pas » ne devient jamais « tout est libre ».
 */
function fetchSlice(rentableId, startMs, endMs, depth) {
    const url = `/fr/api-rentable?rentable=${rentableId}&start=${isoDay(new Date(startMs))}&end=${isoDay(new Date(endMs))}`;

    if (inFlight.has(url)) return inFlight.get(url);

    const request = fetch(url, { signal: AbortSignal.timeout(FEED_TIMEOUT_MS) })
        .then((res) => {
            // `res.ok` n'était pas testé : le proxy Quilium rend une page d'erreur HTML
            // sur une 502, `res.json()` levait, et l'échec devenait un tableau vide.
            if (!res.ok) throw new Error(`flux de disponibilité : HTTP ${res.status}`);
            return res.json();
        })
        .then((rows) => {
            if (!Array.isArray(rows)) return { ok: true, rows: [] };

            if (rows.length < FEED_RESPONSE_LIMIT || depth >= MAX_SPLIT_DEPTH) {
                return { ok: true, rows };
            }

            // Médiane calculée en JOURS, arrondie au supérieur : une plage de 2 jours
            // doit se couper en [j] et [j+1]. Avec une moyenne en millisecondes, `mid`
            // retombait sur le jour de début et la récursion s'arrêtait là, laissant la
            // réponse tronquée.
            const spanDays = Math.round((endMs - startMs) / DAY_MS);
            if (spanDays < 1) return { ok: true, rows };
            const mid = startMs + Math.ceil(spanDays / 2) * DAY_MS;
            if (mid <= startMs || mid > endMs) return { ok: true, rows };

            return Promise.all([
                fetchSlice(rentableId, startMs, mid - DAY_MS, depth + 1),
                fetchSlice(rentableId, mid, endMs, depth + 1),
            ]).then(([a, b]) => ({
                // Une moitié en échec rend TOUTE la tranche incertaine : on ne peut pas
                // affirmer qu'il n'y a rien sur la moitié qu'on n'a pas pu lire.
                ok: a.ok && b.ok,
                rows: a.rows.concat(b.rows),
            }));
        })
        .catch(() => ({ ok: false, rows: [] }))
        .finally(() => {
            // Sans ce retrait, une promesse en ÉCHEC restait servie à tout appel
            // ultérieur de la même URL : le rechargement du mois ne retentait jamais rien.
            inFlight.delete(url);
        });

    inFlight.set(url, request);
    return request;
}

/**
 * Garantit que [startDate, endDate] est chargé, puis rend TOUTES les réservations
 * connues du rentable (le cache est cumulatif : les mois déjà visités restent).
 *
 * @param {string} rentableId
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {Promise<Array>} enregistrements bruts du flux (format FullCalendar)
 */
export function ensureRentableRange(rentableId, startDate, endDate) {
    if (!rentableId || !startDate || !endDate) return Promise.resolve([]);

    const store = entry(rentableId);
    const startMs = dayStart(startDate);
    const endMs = dayStart(endDate);
    if (endMs < startMs) return Promise.resolve(store.rows);

    const gaps = missingRanges(store, startMs, endMs);
    if (!gaps.length) return Promise.resolve(store.rows);

    return Promise.all(gaps.map(([s, e]) => fetchSlice(rentableId, s, e, 0))).then((parts) => {
        // Seules les tranches réellement obtenues sont mémorisées comme chargées.
        // Auparavant TOUTES l'étaient, y compris celles dont l'appel avait échoué :
        // revenir sur le mois ne relançait alors plus rien.
        parts.forEach((part, i) => {
            if (part.ok) rememberRange(store, gaps[i][0], gaps[i][1]);
        });
        addRows(store, [].concat.apply([], parts.map((p) => p.rows)));

        if (parts.some((p) => !p.ok)) {
            store.incomplet = true;
            signalerFluxIncomplet();
        }
        return store.rows;
    });
}

// ── Ignorance assumée ───────────────────────────────────────────────────────
// Un appelant qui ne demande rien de plus obtient les lignes connues, comme avant.
// Celui qui veut savoir si elles sont COMPLÈTES interroge `disponibilitesIncertaines`
// ou s'abonne à l'événement — c'est ce qui permet d'afficher un bandeau plutôt que de
// présenter un calendrier vide comme un calendrier libre.

let fluxSignale = false;

function signalerFluxIncomplet() {
    if (fluxSignale) return; // un seul bandeau, même si plusieurs tranches échouent
    fluxSignale = true;
    window.dispatchEvent(new CustomEvent('gemengapp:disponibilites-incompletes'));
}

/**
 * Les disponibilités chargées sont-elles sûres ?
 *
 * @param {string} [rentableId] limite la question à un rentable ; sinon, tout appel
 *   en échec depuis le chargement de la page suffit à répondre « non ».
 */
export function disponibilitesIncertaines(rentableId) {
    if (rentableId) return !!(cache.get(rentableId) || {}).incomplet;
    return fluxSignale;
}

/**
 * Réservations déjà en cache, sans déclencher d'appel réseau.
 */
export function cachedRentableRows(rentableId) {
    return cache.has(rentableId) ? cache.get(rentableId).rows : [];
}
